import React, { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Wordmark } from "../components/landing/Wordmark";
import { fetchContent } from "../lib/api";
import { applySEO } from "../lib/image";

const WILLKART_EMBED_URL =
    "https://willkart.ai/embed/aad7a9fc-a486-40fa-8593-d837c3fba88e/client";
const IFRAME_RESIZER_SRC =
    "https://cdn.jsdelivr.net/npm/@iframe-resizer/[email protected]";

export default function MakeAWill() {
    const [content, setContent] = useState(null);

    useEffect(() => {
        fetchContent().then((c) => {
            setContent(c);
            applySEO({
                title: `Make a Will · ${c?.brand?.name || "One Tree Hill"}`,
                description:
                    "Create your legal will with WillKart, integrated into One Tree Hill Prime Wealth.",
            });
        });
        // Run once on mount — fetchContent/applySEO are module-stable imports.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the iframe-resizer v5 parent script once and initialise on the iframe.
    useEffect(() => {
        const init = () => {
            // iframe-resizer v5 exposes window.iframeResize (lowercase "i").
            const resize = window.iframeResize || window.iFrameResize;
            if (typeof resize !== "function") return;
            try {
                resize(
                    { log: false, checkOrigin: false, license: "GPLv3" },
                    "#willkart-embed"
                );
            } catch (e) {
                console.debug("iframe-resizer init failed", e);
            }
        };

        const existing = document.querySelector(`script[src="${IFRAME_RESIZER_SRC}"]`);
        if (existing) {
            init();
            return;
        }
        const s = document.createElement("script");
        s.src = IFRAME_RESIZER_SRC;
        s.async = true;
        s.onload = init;
        document.body.appendChild(s);
        return () => {
            // Detach iframe-resizer listeners on unmount so route changes don't leak.
            try {
                const el = document.querySelector("#willkart-embed");
                if (el && el.iFrameResizer) el.iFrameResizer.close();
                if (el && el.iframeResizer) el.iframeResizer.close();
            } catch (e) {
                console.debug("iframe-resizer cleanup failed", e);
            }
        };
    }, []);

    return (
        <div className="min-h-screen bg-[#F5F5F2] flex flex-col" data-testid="makeawill-page">
            <header className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-oth-line/70 bg-white/70 backdrop-blur-md">
                <a href="/" className="inline-flex items-center gap-3 group" data-testid="makeawill-back-home" aria-label="Back to home">
                    <ArrowLeft size={18} className="text-oth-ink2 group-hover:-translate-x-0.5 transition-transform duration-300" />
                    <Wordmark
                        logoUrl={content?.brand?.logo_url}
                        brandName={content?.brand?.name}
                        wordmark={content?.brand?.wordmark}
                        logoSize={content?.brand?.logo_size}
                        nameColor={content?.brand?.name_color}
                        nameFont={content?.brand?.name_font}
                        nameSize={content?.brand?.name_size}
                    />
                </a>
            </header>

            <main className="flex-1 flex">
                <iframe
                    id="willkart-embed"
                    src={WILLKART_EMBED_URL}
                    title="Make a Will"
                    style={{ width: "100%", height: "calc(100vh - 73px)", border: "none", display: "block" }}
                    allow="clipboard-write"
                    data-testid="makeawill-iframe"
                />
            </main>
        </div>
    );
}
